import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token) {
      toast.error('Link de recuperação inválido.');
      return;
    }

    if (password.length < 8) {
      toast.error('A nova senha deve ter no m?nimo 8 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('A confirmação não confere com a nova senha.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      toast.success('Senha redefinida com sucesso.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível redefinir a senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-white flex items-center justify-center p-4" data-testid="reset-password-page">
      <SEOHead
        title="Redefinir senha | BuscaDoutor"
        description="Crie uma nova senha para sua conta BuscaDoutor."
        canonicalUrl="/redefinir-senha"
        robots="noindex, nofollow"
      />

      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <Logo size="lg" />
        </Link>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-8">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary mb-6">
            <ArrowLeft className="w-4 h-4" />
            Voltar para login
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Redefinir senha</h1>
            <p className="text-slate-600">Crie uma nova senha para acessar sua conta.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="pl-10 pr-10 h-12"
                  autoComplete="new-password"
                  required
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar nova senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="pl-10 h-12"
                  autoComplete="new-password"
                  required
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90"
              disabled={loading || !token}
              data-testid="reset-password-submit"
            >
              {loading ? 'Salvando...' : 'Redefinir senha'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
